import { Box, Button, CardsProps, Header, TextContent } from "@amzn/awsui-components-react";
import { CardHeaderWithQuantityInput } from "hooks/useCardTable";
import { CatalogItem, StandardHardwareOrderConfirmationProps, StandardOrderCardSections } from "interfaces";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import useStandardOrder from "hooks/useStandardOrder";
import CardTable from "common/component/CardTable/CardTable";
import { CARD_SKU_TEST_ID, CARD_TAXONOMY_TEST_ID, REMOVE_STANDARD_ITEM_TEST_ID } from "helpers";
import ConfirmActionButton from "common/component/ConfirmActionButton/ConfirmActionButton";

const getStringPath: string = "common.component.LocalItOrderConfirmationContent";
const getStringPathCard: string = `${getStringPath}.card`;
const getStringPathRemove: string = `${getStringPathCard}.removeModal`;

/**
 * Defines which sections should be visible in the card
 *
 * Used to control which information is displayed for each standard hardware item
 * @type {string[]}
 */
export const standardOrderVisibleCardSections: string[] = [
    StandardOrderCardSections.SKU,
    StandardOrderCardSections.TAXONOMY,
    StandardOrderCardSections.CUSTOM_BUTTON,
];

/**
 * Creates a card definition object for rendering non standard hardware item cards
 *
 * @param {(item: CatalogItem) => number} getQuantity - Retrieves the selected quantity for the product
 * @param {(item: CatalogItem, value: number) => void} updateQuantity - Updates the quantity for the selected product
 * @param {(item: CatalogItem) => void} removeItem - Removes the selected product from the list
 * @param {() => boolean} canRemoveItems - Function that returns if items can be removed
 * @param {(id: string) => string | undefined} getHasError - retrieves if an id has an error
 *
 * @returns {object} Card definition object containing header and sections configuration
 */
export const standardOrderCardDefinition = (
    getQuantity: (item: CatalogItem) => number,
    updateQuantity: (item: CatalogItem, value: number) => void,
    removeItem: (item: CatalogItem) => void,
    canRemoveItems: () => boolean,
    getHasError: (id: string) => string | undefined,
): CardsProps.CardDefinition<CatalogItem> => ({
    header: (item: CatalogItem) => {
        const itemQuantity = getQuantity(item);

        return (
            <CardHeaderWithQuantityInput
                name={item.name}
                description={item.description}
                value={itemQuantity}
                setValue={(value: number) => updateQuantity(item, value)}
                quantityErrorText={getHasError(item.taxonomyId)}
                touchpoint="intake-standard-item-quantity"
            />
        );
    },
    sections: [
        {
            id: StandardOrderCardSections.SKU,
            header: getString(`${getStringPathCard}.SKU`),
            content: (item: CatalogItem) => (
                <TextContent data-testid={CARD_SKU_TEST_ID}>
                    {item.sku && item.sku !== "" ? item.sku : getString(`${getStringPathCard}.noSKU`)}
                </TextContent>
            ),
        },
        {
            id: StandardOrderCardSections.TAXONOMY,
            header: getString(`${getStringPathCard}.Taxonomy`),
            content: (item: CatalogItem) => (
                <TextContent data-testid={CARD_TAXONOMY_TEST_ID}>
                    {item.taxonomyId && item.taxonomyId !== ""
                        ? item.taxonomyId
                        : getString(`${getStringPathCard}.noTaxonomy`)}
                </TextContent>
            ),
        },
        {
            id: StandardOrderCardSections.CUSTOM_BUTTON,
            content: (item: CatalogItem) => (
                <Box float="right">
                    <ConfirmActionButton
                        iconAlign="left"
                        iconName="subtract-minus"
                        content={getString(`${getStringPathCard}.removeButton`)}
                        onClick={() => removeItem(item)}
                        disabled={!canRemoveItems()}
                        disabledReason={getString(`${getStringPathCard}.limitReason`)}
                        modalTitle={getString("title", getStringPathRemove, { name: item.name })}
                        modalCancel={getString(`${getStringPathRemove}.cancel`)}
                        modalConfirm={getString(`${getStringPathRemove}.confirm`)}
                        modalDescription={getString(`${getStringPathRemove}.description`)}
                        touchpoint="intake-remove-standard-item"
                    />
                </Box>
            ),
        },
    ],
});

/**
 * Renders the non standard order layout
 *
 * @property {CardsProps.CardsLayout[]} cardsLayout - The cards layout configuration using cloudscape
 * @property {Map<CatalogRequestResponse, number>} standardOrder - The map of standard orders to display
 * @property {React.Dispatch<React.SetStateAction<Map<CatalogRequestResponse, number>>>} setStandardOrder - The setter function of
 * the standardOrder property
 *
 * @returns {JSX.Element} An render that allows the user to view and add standard orders
 */
const StandardHardwareOrderConfirmation = ({
    cardsLayout,
    standardOrder,
    setStandardOrder,
    getHasError,
}: StandardHardwareOrderConfirmationProps) => {
    /* Standard Order Hook */
    const { catalog, trackBy, getQuantity, updateQuantity, removeItem, canRemoveItems } = useStandardOrder({
        standardOrder,
        setStandardOrder,
    });

    return (
        <CardTable<CatalogItem>
            cardDefinitions={standardOrderCardDefinition(
                getQuantity,
                updateQuantity,
                removeItem,
                canRemoveItems,
                getHasError,
            )}
            catalog={catalog}
            cardSectionsVisible={standardOrderVisibleCardSections}
            trackBy={trackBy}
            header={<Header>{getString(`${getStringPath}.header.title`)}</Header>}
            cardsLayout={cardsLayout}
        />
    );
};

export default StandardHardwareOrderConfirmation;
